/**
 * A failed article update, read from server_pray's legacy envelope (`{ data: [error] }`):
 *
 * - 422 `data[0] = { message: 'Unprocessable entity', info: <prose>, code: 'EPISODE_…' }`;
 * - 409 `data[0] = { message: 'Conflict', info: { code: 'EPISODE_ALREADY_LINKED', detail, article } }`;
 * - 400 `data[0] = { message: 'Invalid parameters received', info: <prose> }`, with no code.
 */

export type ArticleHoldingEpisode = {
  id: string;
  title: string;
  slug: string;
};

type ArticleUpdateProblem = {
  status: number;
  code: string | null;
  detail: string | null;
  linkedArticle: ArticleHoldingEpisode | null;
};

export class ArticleUpdateError extends Error {
  readonly status: number;

  /** `EPISODE_…` on a 409 or 422. Null on a failure the server gives no code, such as an unknown category. */
  readonly code: string | null;

  /** The server's prose for the failure. */
  readonly detail: string | null;

  /** On a 409, the article that already has the episode. */
  readonly linkedArticle: ArticleHoldingEpisode | null;

  constructor(problem: ArticleUpdateProblem) {
    super(problem.detail ?? `Request failed with status ${problem.status}`);
    this.name = 'ArticleUpdateError';
    this.status = problem.status;
    this.code = problem.code;
    this.detail = problem.detail;
    this.linkedArticle = problem.linkedArticle;
  }
}

type Payload = Record<string, unknown>;

const payloadOf = (value: unknown): Payload | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Payload) : null;

const stringOrNull = (value: unknown): string | null => (typeof value === 'string' ? value : null);

const holdingArticleOf = (value: unknown): ArticleHoldingEpisode | null => {
  const article = payloadOf(value);
  if (!article) return null;
  const { id, title, slug } = article;
  if (typeof id !== 'string' || typeof title !== 'string' || typeof slug !== 'string') return null;
  return { id, title, slug };
};

/** The HTTP status is the truth; a body that is not the envelope still becomes an error with that status. */
export const toArticleUpdateError = (status: number, body: unknown): ArticleUpdateError => {
  const data = payloadOf(body)?.data;
  const entry = Array.isArray(data) ? payloadOf(data[0]) : null;
  const info = payloadOf(entry?.info);

  return new ArticleUpdateError({
    status,
    code: stringOrNull(info ? info.code : entry?.code),
    detail: stringOrNull(info ? info.detail : entry?.info),
    linkedArticle: holdingArticleOf(info?.article),
  });
};
